import { useCallback, useEffect, useRef, useState } from "react";

import { reverse } from "@reactivated";
import type { ServerCmsTypesProductSummary as ProductSummary } from "@reactivated";

type CatalogFetchState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; products: ProductSummary[] };

interface UseProductCatalogResult {
    fetchState: CatalogFetchState;
    retry: () => void;
}

/** Fetch the live public product catalog. One round-trip on mount. */
export const useProductCatalog = (locale: string): UseProductCatalogResult => {
    const [fetchState, setFetchState] = useState<CatalogFetchState>({
        status: "loading",
    });
    const [attempt, setAttempt] = useState(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const retry = useCallback(() => {
        setAttempt((n) => n + 1);
    }, []);

    useEffect(() => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch effect: reset to loading on each (re)fetch when locale/attempt changes
        setFetchState({ status: "loading" });

        const url = reverse("cms-api:products");
        const params = new URLSearchParams({ locale });

        fetch(`${url}?${params.toString()}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.json();
            })
            .then((data: { products: ProductSummary[] }) => {
                if (controller.signal.aborted) return;
                setFetchState({ status: "ready", products: data.products });
            })
            /* v8 ignore start -- @preserve */
            .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Failed to fetch product catalog:", error);
                setFetchState({ status: "error" });
            });
        /* v8 ignore stop -- @preserve */

        return () => {
            controller.abort();
        };
    }, [locale, attempt]);

    return { fetchState, retry };
};
